'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { Files, Settings } from 'lucide-react';
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone';
import styles from './FileDropzone.module.css';
import { Alert } from './Alert';

export interface FileDropzoneProps {
  /** Line above the link in the resting state, e.g. "Drag and drop a file or". */
  description?: string;
  /** Label of the text link that opens the file picker. */
  buttonLabel?: string;
  /** Small line under the link, e.g. the allowed file types and size. */
  hint?: string;
  /** Accepted types, in react-dropzone's format (MIME type -> extensions). */
  accept?: Accept;
  /** Largest accepted file in bytes. */
  maxSize?: number;
  /** Called with the single accepted file. */
  onFile: (file: File) => void;
  /** Called when a dropped/chosen file is refused (too large, wrong type). */
  onReject?: (rejections: FileRejection[]) => void;
  /**
   * Message for a refused file, shown in the error banner at the top of the
   * zone. Pass a function to word it from the rejection (e.g. "too large" vs
   * "wrong type").
   */
  rejectedMessage?: string | ((rejections: FileRejection[]) => string);
  /** Error from the caller (e.g. the upload failed), shown in the same banner. */
  error?: string | null;
  /** Called when the banner's close button is pressed. */
  onErrorDismiss?: () => void;
  /** Busy state: spinner + `loadingLabel`, and further drops are ignored. */
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Drag-and-drop area for adding one file. Same look and states as the CV upload
 * zone (Add candidate > Upload CV): resting (cloud icon + "browse files" link),
 * dragging ("You got this! Drop it!") and busy (spinner). A refused file (or an
 * `error` from the caller) shows a dismissible error banner at the top of the zone
 * while the rest stays usable. It only hands back the file; the caller uploads it.
 */
export const FileDropzone = React.forwardRef<HTMLDivElement, FileDropzoneProps>(
  (
    {
      description = 'Drag and drop a file or',
      buttonLabel = 'browse files',
      hint,
      accept,
      maxSize,
      onFile,
      onReject,
      rejectedMessage = "That file isn't accepted.",
      error,
      onErrorDismiss,
      loading = false,
      loadingLabel = 'Uploading…',
      disabled = false,
      className,
    },
    ref
  ) => {
    const [rejection, setRejection] = React.useState<string | null>(null);
    const blocked = disabled || loading;

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      multiple: false,
      accept,
      maxSize,
      disabled: blocked,
      noClick: true, // only the link opens the picker
      noKeyboard: true,
      onDropAccepted: (files) => {
        setRejection(null);
        if (files[0]) onFile(files[0]);
      },
      onDropRejected: (rejections) => {
        setRejection(typeof rejectedMessage === 'function' ? rejectedMessage(rejections) : rejectedMessage);
        onReject?.(rejections);
      },
    });

    const { ref: rootRef, ...rootProps } = getRootProps();
    const setRefs = (node: HTMLDivElement | null) => {
      // react-dropzone hands back an object ref for the root element.
      (rootRef as unknown as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    };

    type View = 'busy' | 'dragging' | 'resting';
    const view: View = loading ? 'busy' : isDragActive ? 'dragging' : 'resting';
    const message = rejection ?? error ?? null;
    const dismiss = () => {
      setRejection(null);
      onErrorDismiss?.();
    };

    return (
      <div
        ref={setRefs}
        {...rootProps}
        className={clsx(styles.root, view === 'dragging' && styles.dragging, className)}
        aria-busy={loading || undefined}
      >
        <input {...getInputProps()} />
        {message && (
          <div className={styles.banner}>
            <Alert variant="error" appearance="banner" dismissible onDismiss={dismiss}>
              {message}
            </Alert>
          </div>
        )}
        <div className={styles.icon} aria-hidden="true">
          {view === 'resting' && <img className={styles.iconIn} src="/img/floats/cloud-upload.png" alt="" />}
          {view === 'dragging' && <Files className={styles.iconIn} size={40} />}
          {view === 'busy' && <Settings className={clsx(styles.iconIn, styles.spin)} size={40} />}
        </div>

        <p className={styles.description}>
          {view === 'dragging' && 'You got this! Drop it!'}
          {view === 'busy' && loadingLabel}
          {view === 'resting' && (
            <>
              {description}
              <br />
              <button type="button" className={styles.link} onClick={open} disabled={blocked}>
                {buttonLabel}
              </button>
            </>
          )}
        </p>
        {view === 'resting' && hint && <p className={styles.hint}>{hint}</p>}
      </div>
    );
  }
);

FileDropzone.displayName = 'FileDropzone';

export default FileDropzone;
